/**
 * Gift Selection Store - Store Actions Layer
 * Internal actions for managing gift selection sessions
 *
 * ARCHITECTURE PATTERN:
 * This is the ACTIONS LAYER - internal bridge between store and public API
 * External code should NEVER import from this file directly
 * Use simple-gift-selection-store.public-api.ts instead
 */
import { batch } from '@legendapp/state';
import {
    giftSelectionStore$,
    type GiftSelectionSession,
    type GiftSelectionEntityType,
    createGiftSelectionSession as createSession,
    restoreServerSession as restoreSession,
} from './simple-gift-selection.store';
import { type GiftSelectionTurn, type SelectedGift } from '../types/gift-selection.types';
import { type FreeGiftsData } from '../../../types/front.graphql';

// Shorthand for sessions access
const sessions$ = giftSelectionStore$.sessions;

/**
 * Internal store actions
 * These methods directly access and mutate the store
 */
export const giftSelectionStoreActions = {
    // ========================================================================
    // STATE GETTERS - INTERNAL USE ONLY
    // ========================================================================

    /**
     * Get all sessions from store
     */
    getAllSessions: (): Record<string, GiftSelectionSession> => {
        return sessions$.peek() ?? {};
    },

    /**
     * Get a specific session by ID
     */
    getSessionById: (sessionId: string): GiftSelectionSession | undefined => {
        return sessions$[sessionId]?.peek();
    },

    /**
     * Get all sessions for a specific entity
     */
    getSessionsByEntity: (entityId: string, entityType: GiftSelectionEntityType): GiftSelectionSession[] => {
        const allSessions = sessions$.peek();
        const results: GiftSelectionSession[] = [];

        for (const sessionId in allSessions) {
            if (Object.prototype.hasOwnProperty.call(allSessions, sessionId)) {
                const session = allSessions[sessionId];
                if (session.entityId === entityId && session.entityType === entityType) {
                    results.push(session);
                }
            }
        }

        return results;
    },

    /**
     * Get most recent session for an entity
     */
    getMostRecentSession: (entityId: string, entityType: GiftSelectionEntityType): GiftSelectionSession | undefined => {
        const sessions = giftSelectionStoreActions.getSessionsByEntity(entityId, entityType);
        return sessions.sort((a, b) => b.lastUpdated - a.lastUpdated)[0];
    },

    /**
     * Get turns for a specific session
     */
    getSessionTurns: (sessionId: string): GiftSelectionTurn[] => {
        const session = giftSelectionStoreActions.getSessionById(sessionId);
        return session?.selectionTurns ?? [];
    },

    /**
     * Get available gifts for a session
     */
    getAvailableGifts: (sessionId: string): FreeGiftsData[] => {
        const session = giftSelectionStoreActions.getSessionById(sessionId);
        return session?.availableGifts ?? [];
    },

    /**
     * Get selected gifts for a session
     */
    getSelectedGifts: (sessionId: string): SelectedGift[] => {
        const session = giftSelectionStoreActions.getSessionById(sessionId);
        return session?.selectedGifts ?? [];
    },

    /**
     * Get product quantity for a session
     */
    getProductQuantity: (sessionId: string): number => {
        const session = giftSelectionStoreActions.getSessionById(sessionId);
        return session?.productQuantity ?? 1;
    },

    /**
     * Get remaining quantity after certain round
     */
    getRemainingQuantity: (sessionId: string, upToRound: number): number => {
        const session = giftSelectionStoreActions.getSessionById(sessionId);
        if (!session) return 0;

        const productQuantity = session.productQuantity ?? 1;
        const selectedGifts = session.selectedGifts ?? [];

        const usedQuantity = selectedGifts
            .filter(g => g.selectionRound < upToRound)
            .reduce((acc, gift) => acc + (gift.quantityUsedByRule ?? 0), 0);

        return Math.max(0, productQuantity - usedQuantity);
    },

    /**
     * Get current active session for a specific entity
     * Returns the session marked as active in activeSessionIds
     */
    getActiveSession: (entityId: string, entityType: GiftSelectionEntityType): GiftSelectionSession | undefined => {
        const activeSessionIds = giftSelectionStore$.activeSessionIds.peek();
        const activeSessionId = activeSessionIds[entityType]?.[entityId];

        if (!activeSessionId) {
            return undefined;
        }

        return giftSelectionStoreActions.getSessionById(activeSessionId);
    },

    /**
     * Get active session ID for a specific entity
     * Returns just the session ID without fetching the full session
     */
    getActiveSessionId: (entityId: string, entityType: GiftSelectionEntityType): string | undefined => {
        const activeSessionIds = giftSelectionStore$.activeSessionIds.peek();
        return activeSessionIds[entityType]?.[entityId];
    },

    // ========================================================================
    // MUTATIONS - INTERNAL USE ONLY
    // ========================================================================

    /**
     * Create new session
     */
    createSession: (
        entityId: string,
        entityType: GiftSelectionEntityType,
        availableGifts: FreeGiftsData[],
        maxTurns: number = 3,
        productQuantity?: number
    ): string => {
        return createSession(entityId, entityType, availableGifts, maxTurns, productQuantity);
    },

    /**
     * Restore a session from server-synced data using the server-provided sessionId.
     * Creates a minimal session entry so the gift selection UI can bind to it.
     * No-op if the session already exists.
     */
    restoreServerSession: (
        serverSessionId: string,
        entityId: string,
        entityType: GiftSelectionEntityType,
        productQuantity?: number
    ): void => {
        restoreSession(serverSessionId, entityId, entityType, productQuantity);
    },

    /**
     * Update session data
     */
    updateSession: (sessionId: string, updates: Partial<GiftSelectionSession>): void => {
        const session = sessions$[sessionId]?.peek();
        if (!session) {
            return;
        }

        batch(() => {
            Object.entries(updates).forEach(([key, value]) => {
                if (key !== 'sessionId') {
                    // Don't allow sessionId changes
                    const sessionObservable = sessions$[sessionId];
                    if (sessionObservable && key in sessionObservable) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (sessionObservable as Record<string, any>)[key].set(value);
                    }
                }
            });
            sessions$[sessionId].lastUpdated.set(Date.now());
        });
    },

    /**
     * Add turn to session
     */
    addTurn: (sessionId: string, turn: GiftSelectionTurn): void => {
        const session = sessions$[sessionId]?.peek();
        if (!session) {
            return;
        }

        batch(() => {
            sessions$[sessionId].selectionTurns.push(turn);
            sessions$[sessionId].lastUpdated.set(Date.now());
        });
    },

    /**
     * Update specific turn in session
     */
    updateTurn: (sessionId: string, turnId: string, updates: Partial<GiftSelectionTurn>): void => {
        const session = sessions$[sessionId]?.peek();
        if (!session) {
            return;
        }

        const turnIndex = session.selectionTurns.findIndex(t => t.turnId === turnId);
        if (turnIndex === -1) {
            return;
        }
        batch(() => {
            Object.entries(updates).forEach(([key, value]) => {
                if (key !== 'turnId') {
                    // Don't allow turnId changes
                    const turnObservable = sessions$[sessionId].selectionTurns[turnIndex];
                    if (turnObservable && key in turnObservable) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (turnObservable as Record<string, any>)[key].set(value);
                    }
                }
            });
            sessions$[sessionId].selectionTurns[turnIndex].lastUpdated.set(Date.now());
            sessions$[sessionId].lastUpdated.set(Date.now());
        });
    },

    /**
     * Remove turn from session
     */
    removeTurn: (sessionId: string, turnId: string): void => {
        const session = sessions$[sessionId]?.peek();
        if (!session) {
            console.error('[GiftSelectionStoreActions] Session not found:', sessionId);
            return;
        }

        console.log('[GiftSelectionStoreActions] Removing turn:', { sessionId, turnId });
        batch(() => {
            const updatedTurns = session.selectionTurns.filter(t => t.turnId !== turnId);
            sessions$[sessionId].selectionTurns.set(updatedTurns);
            sessions$[sessionId].lastUpdated.set(Date.now());
        });
    },

    /**
     * Delete session
     */
    deleteSession: (sessionId: string): void => {
        const session = sessions$[sessionId]?.peek();
        if (!session) {
            console.warn('[GiftSelectionStoreActions] Session not found:', sessionId);
            return;
        }

        console.log('[GiftSelectionStoreActions] Deleting session:', sessionId);
        sessions$[sessionId].delete();
    },

    /**
     * Delete all sessions for entity
     */
    deleteEntitySessions: (entityId: string, entityType: GiftSelectionEntityType): void => {
        const allSessions = sessions$.peek();

        console.log('[GiftSelectionStoreActions] Deleting entity sessions:', { entityId, entityType });
        batch(() => {
            for (const sessionId in allSessions) {
                if (Object.prototype.hasOwnProperty.call(allSessions, sessionId)) {
                    const session = allSessions[sessionId];
                    if (session.entityId === entityId && session.entityType === entityType) {
                        sessions$[sessionId].delete();
                    }
                }
            }
        });
    },

    /**
     * Reset all sessions
     */
    resetAllSessions: (): void => {
        console.log('[GiftSelectionStoreActions] Resetting all sessions');
        sessions$.set({});
    },

    /**
     * Set active session for an entity
     * Marks a session as the currently active session for the entity
     */
    setActiveSession: (sessionId: string, entityId: string, entityType: GiftSelectionEntityType): void => {
        const session = sessions$[sessionId]?.peek();
        if (!session) {
            console.error('[GiftSelectionStoreActions] Session not found:', sessionId);
            return;
        }

        console.log('[GiftSelectionStoreActions] Setting active session:', { sessionId, entityId, entityType });
        giftSelectionStore$.activeSessionIds[entityType][entityId].set(sessionId);
    },

    /**
     * Clear active session for an entity
     */
    clearActiveSession: (entityId: string, entityType: GiftSelectionEntityType): void => {
        console.log('[GiftSelectionStoreActions] Clearing active session:', { entityId, entityType });
        giftSelectionStore$.activeSessionIds[entityType][entityId].delete();
    },

    /**
     * Set UI state
     */
    setPopupOpen: (sessionId: string, isOpen: boolean): void => {
        const session = sessions$[sessionId]?.peek();
        if (!session) return;

        sessions$[sessionId].isPopupOpen.set(isOpen);
    },

    /**
     * Set gift options modal state for specific turn
     */
    setShowGiftOptionsForTurn: (sessionId: string, turnId?: string): void => {
        const session = sessions$[sessionId]?.peek();
        if (!session) return;

        sessions$[sessionId].showGiftOptionsForTurn.set(turnId);
    },

    // ========================================================================
    // SELECTED GIFTS MUTATIONS
    // ========================================================================

    /**
     * Set selected gifts for a session
     */
    setSelectedGifts: (sessionId: string, gifts: SelectedGift[]): void => {
        const session = sessions$[sessionId]?.peek();
        if (!session) {
            console.error('[GiftSelectionStoreActions] Session not found:', sessionId);
            return;
        }

        console.log('[GiftSelectionStoreActions] Setting selected gifts:', { sessionId, count: gifts.length });
        batch(() => {
            sessions$[sessionId].selectedGifts.set(gifts);
            sessions$[sessionId].lastUpdated.set(Date.now());
        });
    },

    /**
     * Add or update a gift in the session
     */
    addOrUpdateGift: (sessionId: string, gift: SelectedGift): void => {
        const session = sessions$[sessionId]?.peek();
        if (!session) {
            console.error('[GiftSelectionStoreActions] Session not found:', sessionId);
            return;
        }

        const currentGifts = session.selectedGifts ?? [];
        const giftIndex = currentGifts.findIndex(g => g.selectionRound === gift.selectionRound);

        console.log('[GiftSelectionStoreActions] Adding/updating gift:', { sessionId, giftId: gift.id });
        batch(() => {
            if (giftIndex >= 0) {
                const updatedGifts = [...currentGifts];
                updatedGifts[giftIndex] = gift;
                sessions$[sessionId].selectedGifts.set(updatedGifts);
            } else {
                sessions$[sessionId].selectedGifts.set([...currentGifts, gift]);
            }
            sessions$[sessionId].lastUpdated.set(Date.now());
        });
    },

    /**
     * Remove a gift by ID
     */
    removeGift: (sessionId: string, giftId: string): void => {
        const session = sessions$[sessionId]?.peek();
        if (!session) {
            console.error('[GiftSelectionStoreActions] Session not found:', sessionId);
            return;
        }

        const currentGifts = session.selectedGifts ?? [];
        const updatedGifts = currentGifts.filter(g => g.id !== giftId);

        console.log('[GiftSelectionStoreActions] Removing gift:', { sessionId, giftId });
        batch(() => {
            sessions$[sessionId].selectedGifts.set(updatedGifts);
            sessions$[sessionId].lastUpdated.set(Date.now());
        });
    },

    /**
     * Clear all selected gifts
     */
    clearSelectedGifts: (sessionId: string): void => {
        const session = sessions$[sessionId]?.peek();
        if (!session) {
            console.error('[GiftSelectionStoreActions] Session not found:', sessionId);
            return;
        }

        console.log('[GiftSelectionStoreActions] Clearing selected gifts:', sessionId);
        batch(() => {
            sessions$[sessionId].selectedGifts.set([]);
            sessions$[sessionId].lastUpdated.set(Date.now());
        });
    },
} as const;

export type GiftSelectionStoreActions = typeof giftSelectionStoreActions;
